import React, { useCallback } from 'react';
import { Card, Form, Input, Button, Select, Typography, Space, message } from 'antd';
import { beautifyEntry } from '../../KnowledgeDatabaseServices';
import { useTranslation } from 'react-i18next';
import BeautifyControl from '../../../Shared/BeautifyControl/BeautifyControl';
import { HighlightOutlined } from '@ant-design/icons';
import { ICON_SIZE } from '../../../Shared/Styles/iconButton';

const { TextArea } = Input;
const { Title } = Typography;
const NewEntryForm = ({ onSubmit, editingEntry, loading, formRef, onCancel, knowledgeBaseId }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [beautifyResetSignal, setBeautifyResetSignal] = React.useState(0);

    const resetBeautifyControl = useCallback(() => {
        setBeautifyResetSignal((current) => current + 1);
    }, []);

    React.useEffect(() => {
        if (editingEntry) {
            form.setFieldsValue({
                entryType: editingEntry.entry_type === 'question_answer' ? 'qa' : 'business',
                question: editingEntry.data.question,
                answer: editingEntry.data.answer,
                content: editingEntry.data.content
            });
        }
        resetBeautifyControl();
    }, [editingEntry, resetBeautifyControl]);

    const handleSubmit = async (values) => {
        const success = await onSubmit(values, form);
        if (success === false) return;
        // Reset only content fields while preserving entry type
        const currentEntryType = form.getFieldValue('entryType');
        form.resetFields(['question', 'answer', 'content']);
        form.setFieldsValue({ entryType: currentEntryType });
        resetBeautifyControl();
    };

    const handleCancel = () => {
        form.resetFields();
        resetBeautifyControl();
        if (onCancel) {
            onCancel();
        }
    };

    const buildBeautifyPayload = useCallback((type, value) => {
        if (type === 'qa-both') {
            const question = value?.question ?? '';
            const answer = value?.answer ?? '';
            return {
                entry_type: 'question_answer',
                question,
                answer,
            };
        }
        // business
        return {
            entry_type: 'business_info',
            content: value,
        };
    }, []);

    const createRunBeautify = useCallback((kind) => {
        return async (text) => {
            if (!knowledgeBaseId) {
                message.error(t('no_database_id_provided'));
                return { suggestions: [] };
            }

            const values = form.getFieldsValue();
            const payload = buildBeautifyPayload(
                kind,
                kind === 'qa-both'
                    ? { question: values.question, answer: values.answer }
                    : text
            );

            try {
                const data = await beautifyEntry(knowledgeBaseId, payload);
                const rawWarnings = data?.data?.warnings || [];
                const warnings = Array.isArray(rawWarnings) ? rawWarnings : [];

                const rawSuggestions = data?.data?.suggestions || data?.suggestions || [];
                let suggestionsList;

                if (kind === 'business') {
                    // Single-text business info entry
                    suggestionsList = rawSuggestions.map(s => s?.content ?? s);
                } else if (kind === 'qa-both') {
                    // QA entries: backend returns objects { question: string, answer: string }
                    suggestionsList = rawSuggestions.map(s => ({
                        question: s?.question ?? '',
                        answer: s?.answer ?? '',
                    }));
                } else {
                    // Fallback for any other kinds (currently not used)
                    suggestionsList = rawSuggestions.map(s => s?.content ?? s);
                }

                if (!suggestionsList.length) {
                    message.info(t('no_suggestions_available'));
                    return { suggestions: [] };
                }
                const coinsAmount = Math.abs(data?.coins_amount ?? data?.data?.coins_amount ?? 0);
                const analysisText = data?.analysis ?? data?.data?.analysis ?? '';

                return {
                    suggestions: suggestionsList,
                    coinsAmount,
                    analysis: analysisText,
                    warnings,
                };
            } catch (error) {
                message.error(error?.response?.data?.message || t('something_went_wrong'));
                return { suggestions: [] };
            }
        };
    }, [buildBeautifyPayload, form, knowledgeBaseId, t]);

    return (
        <Card
            style={{
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px'
            }}
            styles={{
                header: {
                    minHeight: 'auto',
                    paddingTop: 16,
                    paddingBottom: 16,
                },
                title: {
                    whiteSpace: 'normal',
                    overflow: 'visible',
                },
            }}
            title={
                <Title
                    level={3}
                    style={{
                        margin: 0,
                        fontSize: 24,
                        lineHeight: 1.25,
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {editingEntry ? t('edit_entry') : t('add_new_entry')}
                </Title>
            }
            ref={formRef}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ entryType: 'qa' }}
                style={{ marginBottom: 0 }}
            >
                <Space direction="vertical" style={{ width: '100%', gap: 8 }} size="large">
                    <Form.Item
                        name="entryType"
                        label={t('entry_type')}
                        rules={[{ required: true, message: t('please_select_entry_type') }]}
                        style={{ marginBottom: 0 }}
                    >
                        <Select>
                            <Select.Option value="qa">{t('qa_entry')}</Select.Option>
                            <Select.Option value="business">{t('business_info')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        style={{ marginBottom: 0 }}
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.entryType !== currentValues.entryType
                        }
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('entryType') === 'qa' ? (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Form.Item
                                            name="question"
                                            label={t('question')}
                                            style={{ marginBottom: 0 }}
                                            rules={[{ required: true, message: t('please_enter_question') }]}
                                        >
                                            <TextArea
                                                autoSize={{ minRows: 2 }}
                                                placeholder={t('enter_question_here')}
                                                dir="auto"
                                                style={{ unicodeBidi: 'plaintext' }}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            name="answer"
                                            style={{ marginBottom: 0 }}
                                            label={t('answer')}
                                            rules={[{ required: true, message: t('please_enter_answer') }]}
                                        >
                                            <TextArea
                                                autoSize={{ minRows: 3 }}
                                                placeholder={t('enter_answer_here')}
                                                dir="auto"
                                                style={{ unicodeBidi: 'plaintext' }}
                                            />
                                        </Form.Item>
                                    </Space>
                                </Space>
                            ) : (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Form.Item
                                        name="content"
                                        label={t('business_information')}
                                        style={{ marginBottom: 0 }}
                                        rules={[{ required: true, message: t('please_enter_business_information') }]}
                                    >
                                        <TextArea
                                            autoSize={{ minRows: 4 }}
                                            placeholder={t('enter_business_information_here')}
                                            dir="auto"
                                            style={{ unicodeBidi: 'plaintext' }}
                                        />
                                    </Form.Item>
                                </Space>
                            )
                        }
                    </Form.Item>

                    <Form.Item noStyle>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, current) =>
                                prev.entryType !== current.entryType ||
                                prev.question !== current.question ||
                                prev.answer !== current.answer ||
                                prev.content !== current.content
                            }
                        >
                            {({ getFieldValue }) => {
                                const entryType = getFieldValue('entryType');

                                if (entryType === 'qa') {
                                    const question = (getFieldValue('question') || '').trim();
                                    const answer = (getFieldValue('answer') || '').trim();
                                    const beautifyDisabled = !question || !answer;

                                    return (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                            <BeautifyControl
                                                key="qa-both-beautify"
                                                value={{
                                                    question: getFieldValue('question'),
                                                    answer: getFieldValue('answer'),
                                                }}
                                                onChange={(next) => {
                                                    if (!next) return;
                                                    const currentValues = form.getFieldsValue();
                                                    form.setFieldsValue({
                                                        question: next.question ?? currentValues.question,
                                                        answer: next.answer ?? currentValues.answer,
                                                    });
                                                }}
                                                runBeautify={createRunBeautify('qa-both')}
                                                beautifyLabel={t('beautify')}
                                                beautifyIcon={<HighlightOutlined style={{ fontSize: ICON_SIZE }} />}
                                                revertLabel={t('revert')}
                                                disabled={beautifyDisabled}
                                                buttonStyle={{ color: '#7F56D9' }}
                                                resetSignal={beautifyResetSignal}
                                            />
                                        </div>
                                    );
                                }

                                if (entryType === 'business') {
                                    const content = (getFieldValue('content') || '').trim();
                                    const beautifyDisabled = !content;

                                    return (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                            <BeautifyControl
                                                key="business-content-beautify"
                                                value={getFieldValue('content')}
                                                onChange={(text) => form.setFieldsValue({ content: text })}
                                                runBeautify={createRunBeautify('business')}
                                                beautifyLabel={t('beautify')}
                                                beautifyIcon={<HighlightOutlined style={{ fontSize: ICON_SIZE }} />}
                                                revertLabel={t('revert')}
                                                disabled={beautifyDisabled}
                                                buttonStyle={{ color: '#7F56D9' }}
                                                resetSignal={beautifyResetSignal}
                                            />
                                        </div>
                                    );
                                }

                                return null;
                            }}
                        </Form.Item>

                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                gap: 8,
                            }}
                        >
                            {editingEntry && (
                                <Button type="default" onClick={handleCancel} block>
                                    {t('cancel')}
                                </Button>
                            )}
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                            >
                                {editingEntry ? t('update_entry') : t('add_entry')}
                            </Button>
                        </div>
                    </Form.Item>
                </Space>
            </Form>
        </Card>
    );
};

export default NewEntryForm;
